<template>
    <div class="max-w-5xl mx-auto py-2 md:py-4 px-0 md:px-4 relative">
        <!-- Hero Section -->
        <div>
            <!-- Premium Badge -->
            <div class="mb-4">
                <span
                    class="inline-flex items-center gap-2 pl-2 pr-3 py-1 rounded-full border border-orange-200"
                >
                    <Icon icon="star" class="w-4 h-4 text-10x-orange" />
                    <span class="text-sm font-medium text-10x-orange">
                        Premium
                    </span>
                </span>
            </div>

            <!-- Title -->
            <h1 class="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                10xTravel+ membership
            </h1>
            <p class="text-sm text-slate-500 dark:text-slate-400">
                Get the most out of your cards and rewards
            </p>
        </div>

        <!-- Your community -->
        <section class="mt-10 mb-10">
            <h2 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                Your community
            </h2>
            <div
                class="border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm p-px bg-white dark:bg-slate-800"
            >
                <div
                    class="community-card flex items-stretch overflow-hidden hover:shadow-md dark:hover:shadow-slate-900/50 transition-shadow rounded-lg"
                >
                    <div class="flex flex-col justify-between p-5 flex-1">
                        <div>
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
                                Members-only travel community
                            </h3>
                            <p class="text-sm text-slate-500 dark:text-slate-400 max-w-md">
                                Insider tips, deals, and strategies from top
                                travelers and 10xTravel+ experts
                            </p>
                        </div>
                        <div class="flex items-center gap-4 mt-4">
                            <a
                                :href="circleUrl"
                                target="_blank"
                                class="btn-base btn-10x-orange !mt-0"
                            >
                                Access community
                            </a>
                            <button
                                type="button"
                                class="text-sm font-medium text-slate-800 dark:text-slate-100"
                                @click="showCommunityModal = true"
                            >
                                Learn more
                            </button>
                        </div>
                    </div>
                    <div
                        class="hidden md:flex items-center justify-end shrink-0 w-2/5 mt-8 mr-4"
                    >
                        <img
                            src="/images/community-preview-mask.png"
                            alt="Growth Network community"
                            class="w-full h-full object-cover object-left"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- Your benefits -->
        <section class="mb-10">
            <h2 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                Your benefits
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Legacy benefit: Free Award Booking -->
                <div
                    v-if="isLegacySubscriber"
                    class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm overflow-hidden p-4 min-h-36 flex flex-col justify-between"
                >
                    <div
                        class="absolute inset-x-0.5 top-0.5 h-28 bg-gradient-to-b from-amber-50 dark:from-amber-500/10 to-transparent rounded pointer-events-none"
                    ></div>
                    <div class="relative z-10">
                        <div class="flex items-start justify-between">
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 pr-8">
                                Free award booking
                            </h3>
                            <span
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 shrink-0"
                            >
                                1 / year
                            </span>
                        </div>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-64">
                            Round trip for 2 people, issued as a $275 credit
                            towards your bookings
                        </p>
                    </div>
                    <div class="flex flex-col relative z-10 mt-2">
                        <div class="flex items-center gap-2">
                            <Icon icon="star" class="w-4 h-4 text-amber-500" />
                            <span class="text-sm font-medium text-slate-600 dark:text-slate-300">
                                Legacy member benefit
                            </span>
                        </div>
                        <a
                            href="https://10xtravel.com/award-booking/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-sm font-medium text-slate-800 dark:text-slate-100 mt-3 hover:text-slate-900 dark:text-slate-100"
                        >
                            Learn more
                        </a>
                    </div>
                </div>

                <!-- Legacy benefit: 1:1 Consults -->
                <div
                    v-if="isLegacySubscriber"
                    class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm overflow-hidden p-4 min-h-36 flex flex-col justify-between"
                >
                    <div
                        class="absolute inset-x-0.5 top-0.5 h-28 bg-gradient-to-b from-amber-50 dark:from-amber-500/10 to-transparent rounded pointer-events-none"
                    ></div>
                    <div class="relative z-10">
                        <div class="flex items-start justify-between">
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 pr-8">
                                1:1 Consults
                            </h3>
                            <span
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 shrink-0"
                            >
                                2 / year
                            </span>
                        </div>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-64">
                            Personal consultations with 10xTravel staff during
                            office hours
                        </p>
                    </div>
                    <div class="flex flex-col relative z-10 mt-2">
                        <div class="flex items-center gap-2">
                            <Icon icon="star" class="w-4 h-4 text-amber-500" />
                            <span class="text-sm font-medium text-slate-600 dark:text-slate-300">
                                Legacy member benefit
                            </span>
                        </div>
                        <a
                            href="https://calendly.com/10xt-team/plus-consult"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-sm font-medium text-slate-800 dark:text-slate-100 mt-3 hover:text-slate-900 dark:text-slate-100"
                        >
                            Learn more
                        </a>
                    </div>
                </div>

                <!-- Unlimited benefits tracker -->
                <div
                    class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm overflow-hidden p-4 min-h-36 flex flex-col justify-between"
                >
                    <div
                        class="absolute inset-x-0.5 top-0.5 h-28 bg-gradient-to-b from-slate-50 dark:from-slate-900/40 to-transparent rounded pointer-events-none"
                    ></div>
                    <div class="relative z-10">
                        <div class="flex items-start justify-between">
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 pr-8">
                                Unlimited benefits tracker
                            </h3>
                            <Link href="/card-benefits" class="shrink-0">
                                <Icon
                                    icon="chevron-right"
                                    class="w-5 h-5 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                                />
                            </Link>
                        </div>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-60">
                            Track credit card perks without limits
                        </p>
                    </div>
                    <button
                        type="button"
                        class="text-sm font-medium text-slate-800 dark:text-slate-100 relative z-10 text-left"
                        @click="showBenefitTrackingModal = true"
                    >
                        Learn more
                    </button>
                    <!-- Coin + Calendar illustration -->
                    <div
                        class="absolute -bottom-8 -right-8 pointer-events-none w-42 h-42"
                    >
                        <img
                            src="../../../images/coin-cal.png"
                            alt="Unlimited benefits tracker"
                            class="w-full h-full object-contain"
                        />
                    </div>
                </div>

                <!-- Award alerts -->
                <div
                    class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm overflow-hidden p-4 min-h-36 flex flex-col justify-between"
                >
                    <div
                        class="absolute inset-x-0.5 top-0.5 h-28 bg-gradient-to-b from-slate-50 dark:from-slate-900/40 to-transparent rounded pointer-events-none"
                    ></div>
                    <div class="relative z-10">
                        <div class="flex items-start justify-between">
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 pr-8">
                                Award alerts
                            </h3>
                            <a
                                :href="circleUrl"
                                target="_blank"
                                class="shrink-0"
                            >
                                <Icon
                                    icon="chevron-right"
                                    class="w-5 h-5 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                                />
                            </a>
                        </div>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-60">
                            Get notifications about award availability
                        </p>
                    </div>
                    <button
                        type="button"
                        class="text-sm font-medium text-slate-800 dark:text-slate-100 relative z-10 text-left"
                        @click="showAwardAlertModal = true"
                    >
                        Learn more
                    </button>
                    <!-- Award alerts background -->
                    <div class="absolute bottom-0 right-2 pointer-events-none">
                        <img
                            src="../../../images/award-alerts-card-bg.png"
                            alt="Award alerts"
                            class="w-30"
                        />
                    </div>
                </div>

                <!-- Import redemptions from email -->
                <div
                    class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm overflow-hidden p-4 min-h-36 flex flex-col justify-between"
                >
                    <div
                        class="absolute inset-x-0.5 top-0.5 h-28 bg-gradient-to-b from-slate-50 dark:from-slate-900/40 to-transparent rounded pointer-events-none"
                    ></div>
                    <div class="relative z-10">
                        <div class="flex items-start justify-between">
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 pr-8">
                                Import redemptions from email
                            </h3>
                            <Link href="/trips" class="shrink-0">
                                <Icon
                                    icon="chevron-right"
                                    class="w-5 h-5 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                                />
                            </Link>
                        </div>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-60">
                            Add trips by forwarding your hotel and flight
                            confirmations
                        </p>
                    </div>
                    <button
                        type="button"
                        class="text-sm font-medium text-slate-800 dark:text-slate-100 relative z-10 text-left"
                        @click="showImportRedemptionsModal = true"
                    >
                        Learn more
                    </button>
                    <!-- Document/ticket illustration -->
                    <div
                        class="absolute -bottom-4 right-0 pointer-events-none w-32 h-32"
                    >
                        <img
                            src="../../../images/plus-cards.png"
                            alt="10xTravel+"
                            class="w-full h-full object-cover"
                        />
                    </div>
                </div>

                <!-- Best credit cards to use -->
                <div
                    class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm overflow-hidden p-4 min-h-36 flex flex-col justify-between"
                >
                    <div
                        class="absolute inset-x-0.5 top-0.5 h-28 bg-gradient-to-b from-slate-50 dark:from-slate-900/40 to-transparent rounded pointer-events-none"
                    ></div>
                    <div class="relative z-10">
                        <div class="flex items-start justify-between">
                            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 pr-8">
                                Best credit cards to use
                            </h3>
                            <Link href="/wallet/best-cards" class="shrink-0">
                                <Icon
                                    icon="chevron-right"
                                    class="w-5 h-5 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                                />
                            </Link>
                        </div>
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-60">
                            See which card earns you the most for each spending
                            category
                        </p>
                    </div>
                    <button
                        type="button"
                        class="text-sm font-medium text-slate-800 dark:text-slate-100 relative z-10 text-left"
                        @click="showBestCardsModal = true"
                    >
                        Learn more
                    </button>
                    <!-- Stacked credit cards illustration -->
                    <div
                        class="absolute bottom-0 right-0 pointer-events-none w-36 h-36"
                    >
                        <img
                            src="../../../images/cards-multipliers.png"
                            alt="Best credit cards to use"
                            class="w-full h-full object-cover"
                        />
                    </div>
                </div>
                <!-- Transfer bonus alerts -->
                <div
                    v-if="transferBonusAlertsEnabled"
                    class="relative bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden p-4 min-h-36 flex flex-col justify-between"
                >
                    <div
                        class="absolute inset-x-0.5 top-0.5 h-28 bg-gradient-to-b from-slate-50 to-transparent rounded pointer-events-none"
                    ></div>
                    <div class="relative z-10">
                        <div class="flex items-start justify-between">
                            <h3 class="text-lg font-medium text-slate-800 pr-8">
                                Transfer bonus alerts
                            </h3>
                            <Link
                                href="/resources/transfer-partners-guide"
                                class="shrink-0"
                            >
                                <Icon
                                    icon="chevron-right"
                                    class="w-5 h-5 text-slate-400 hover:text-slate-600"
                                />
                            </Link>
                        </div>
                        <p class="text-sm text-slate-500 mt-1 max-w-60">
                            Get a heads-up when your points will go further with
                            transfer bonuses
                        </p>
                    </div>
                    <button
                        type="button"
                        class="text-sm font-medium text-slate-800 relative z-10 text-left"
                        @click="showTransferBonusAlertsModal = true"
                    >
                        Learn more
                    </button>
                    <div
                        class="absolute -bottom-4 right-0 pointer-events-none w-40 h-40"
                    >
                        <img
                            src="/images/transfer-bonus-alert.png"
                            alt="Transfer bonus alerts"
                            class="w-full h-full object-contain"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- Your plan details -->
        <section class="mb-10">
            <h2 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                Your plan details
            </h2>
            <div
                class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm px-4 md:px-5 py-4"
            >
                <div class="flex flex-col gap-1">
                    <div class="flex items-center py-2">
                        <img
                            src="/images/plus-dashboard/travelplus-logo.svg"
                            alt="10xTravel+"
                            class="h-4 w-36"
                        />
                    </div>
                    <span
                        v-if="isFoundersClubSubscriber"
                        class="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-full px-2.5 py-0.5"
                    >
                        ★ Founders Club — Lifetime Price Lock
                    </span>
                    <p class="text-sm text-slate-500 dark:text-slate-400">
                        {{ renewalText }}
                    </p>
                </div>
                <a
                    href="/settings/plan-billing"
                    class="w-full md:w-auto inline-flex items-center justify-center px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                >
                    Manage subscription
                </a>
            </div>
        </section>

        <!-- Learn More Modals -->
        <BenefitTrackingModal v-model="showBenefitTrackingModal" />
        <AwardAlertModal
            v-model="showAwardAlertModal"
            :explore-url="circleUrl"
        />
        <ImportRedemptionsModal v-model="showImportRedemptionsModal" />
        <BestCardsModal v-model="showBestCardsModal" />
        <TransferBonusAlertsModal
            v-if="transferBonusAlertsEnabled"
            v-model="showTransferBonusAlertsModal"
        />
        <CommunityModal v-model="showCommunityModal" :explore-url="circleUrl" />
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Link, usePage } from '@inertiajs/vue3';
import { useTravelPlusStore } from '@/store/travelplus';
import Icon from '@/components/ui/icon/icon.vue';
import AwardAlertModal from '@/components/travel-plus/Modal/AwardAlertModal.vue';
import BenefitTrackingModal from '@/components/travel-plus/Modal/BenefitTrackingModal.vue';
import BestCardsModal from '@/components/travel-plus/Modal/BestCardsModal.vue';
import ImportRedemptionsModal from '@/components/travel-plus/Modal/ImportRedemptionsModal.vue';
import CommunityModal from '@/components/travel-plus/Modal/CommunityModal.vue';
import TransferBonusAlertsModal from '@/components/travel-plus/Modal/TransferBonusAlertsModal.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import dayjs from 'dayjs';

const { transferBonusAlertsEnabled } = useFeatureFlags();

const showBenefitTrackingModal = ref(false);
const showAwardAlertModal = ref(false);
const showImportRedemptionsModal = ref(false);
const showBestCardsModal = ref(false);
const showTransferBonusAlertsModal = ref(false);
const showCommunityModal = ref(false);

const props = defineProps({
    circleUrl: {
        type: String,
        default: '',
    },
});

const page = usePage();
const isLegacySubscriber = computed(
    () => page.props.is_legacy_subscriber === true
);
const isFoundersClubSubscriber = computed(
    () => page.props.is_founders_club_subscriber === true
);

const travelPlusStore = useTravelPlusStore();
const { subscription, subscriptionEnds } = storeToRefs(travelPlusStore);

onMounted(() => {
    if (!subscription.value) {
        travelPlusStore.getSubscription();
    }
});

const subscriptionPrice = computed(() => {
    const amount = subscription.value?.plan_amount;
    if (amount != null) {
        return Number.isInteger(amount)
            ? `$${amount}`
            : `$${amount.toFixed(2)}`;
    }
    return null;
});

const renewalText = computed(() => {
    if (subscription.value?.on_grace_period) {
        return `Access until ${subscriptionEnds.value}`;
    }
    if (subscriptionEnds.value) {
        const priceText = subscriptionPrice.value
            ? ` for ${subscriptionPrice.value}`
            : '';
        return `Renewing ${subscriptionEnds.value}${priceText}`;
    }
    return 'Active subscription';
});
</script>

<style scoped>
.community-card {
    background: linear-gradient(
        180deg,
        #f8fafc 0%,
        rgba(248, 250, 252, 0) 100%
    );
}
:where(.dark) .community-card {
    background: linear-gradient(
        180deg,
        rgba(30, 41, 59, 0.6) 0%,
        rgba(30, 41, 59, 0) 100%
    );
}
</style>
